import { useMarketDataSelection } from "../../shared/api/market-data-selection.js";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "../../shared/ui/button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import {
  backtestRequestForRevision,
  strategyBacktestFormValues,
  intervalLabels,
  supportedBacktestIntervals,
  type BacktestFormValues,
} from "./backtest-form.js";
import {
  isMonthlyStrategy,
  requiredBacktestInterval,
  type BotRevisionView,
  type BotView,
} from "./models.js";
import { ProblemNotice } from "./problem-notice.js";
import { useBacktests, useDeleteBacktest, useLaunchBacktest } from "./backtest-queries.js";

export function BotBacktestsPanel({
  bot,
  revision,
}: {
  readonly bot: BotView;
  readonly revision: BotRevisionView | undefined;
}) {
  const navigate = useNavigate();
  const selection = useMarketDataSelection();
  const dxlink = selection.data?.backtestProvider === "dxlink";
  const history = useBacktests(bot.id);
  const launch = useLaunchBacktest(bot.id);
  const deleteBacktest = useDeleteBacktest(bot.id);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string>();
  const requiredInterval = requiredBacktestInterval(revision);
  const monthly = isMonthlyStrategy(revision?.strategy);
  const intervals = supportedBacktestIntervals(revision);
  const form = useForm<BacktestFormValues>({
    defaultValues: strategyBacktestFormValues(revision),
  });
  useEffect(() => {
    form.reset(strategyBacktestFormValues(revision));
  }, [form, revision]);
  useEffect(() => {
    if (dxlink) form.setValue("adjustment", "raw");
  }, [dxlink, form, revision]);
  const submit = form.handleSubmit(async (values) => {
    const result = await launch.mutateAsync(
      backtestRequestForRevision(
        dxlink ? { ...values, adjustment: "raw" } : values,
        bot.version,
        revision,
      ),
    );
    void navigate(`/bots/${bot.id}/backtests/${result.id}`);
  });

  return (
    <div className="content-card section-stack backtest-zone">
      <div className="connection-heading">
        <div>
          <p className="eyebrow">Research only</p>
          <h2>Backtests</h2>
          <p className="section-description">
            Every run freezes revision {bot.activeRevisionId.slice(0, 8)} and cannot place broker
            orders.
          </p>
        </div>
        <Button
          disabled={bot.state !== "ready" || launch.isPending}
          onClick={() => setFormOpen((current) => !current)}
          tone="quiet"
        >
          {formOpen ? "Close launcher" : "Launch backtest"}
        </Button>
      </div>
      {bot.state !== "ready" ? (
        <p className="field-hint">Mark the Bot ready before launching a backtest.</p>
      ) : null}
      {formOpen ? (
        <form className="form-stack" onSubmit={(event) => void submit(event)}>
          {monthly ? (
            <p className="field-hint">
              Monthly trend/cash uses your selected 1-minute, 5-minute, 15-minute or daily source
              bars, raw prices and next-session-open fills. Warm-up is automatic. Base modeled costs
              are prefilled; add 5 basis points per fill for stress. Each bot uses an independent
              account, not a shared ETF portfolio.
            </p>
          ) : null}
          <p className="field-hint">
            Data provider:{" "}
            {selection.isPending
              ? "Loading�"
              : selection.isError
                ? "Unavailable � reload to try again"
                : dxlink
                  ? "tastytrade DXLink � native candles; limited intraday history including warm-up."
                  : "Alpaca"}
          </p>
          <div className="field-grid">
            <div className="field-stack">
              <label htmlFor="backtest-from">From</label>
              <input
                id="backtest-from"
                type="date"
                {...form.register("from", { required: true })}
              />
            </div>
            <div className="field-stack">
              <label htmlFor="backtest-to">To</label>
              <input id="backtest-to" type="date" {...form.register("to", { required: true })} />
            </div>
            <div className="field-stack">
              <label htmlFor="backtest-interval">Bar interval</label>
              <select id="backtest-interval" {...form.register("interval")}>
                {intervals.map((interval) => (
                  <option key={interval} value={interval}>
                    {intervalLabels[interval]}
                  </option>
                ))}
              </select>
              {requiredInterval === undefined ? null : (
                <span className="field-hint">
                  The active strategy requires {intervalLabels[requiredInterval]} source bars.
                </span>
              )}
            </div>
            <div className="field-stack">
              <label htmlFor="backtest-adjustment">Price adjustment</label>
              <select id="backtest-adjustment" {...form.register("adjustment")}>
                {monthly || dxlink ? null : <option value="all">Splits and dividends</option>}
                {monthly || dxlink ? null : <option value="split">Splits only</option>}
                <option value="raw">{dxlink ? "Native DXLink candles" : "Raw"}</option>
              </select>
            </div>
            <div className="field-stack">
              <label htmlFor="initial-cash">Initial cash, USD</label>
              <input
                id="initial-cash"
                inputMode="decimal"
                {...form.register("initialCashUsd", { required: true })}
              />
            </div>
            <div className="field-stack">
              <label htmlFor="fill-timing">Fill timing</label>
              <select id="fill-timing" {...form.register("fillTiming")}>
                <option value="next-bar-open">Next bar open</option>
                {monthly ? null : <option value="bar-close">Bar close</option>}
              </select>
            </div>
            <div className="field-stack">
              <label htmlFor="slippage">Slippage, basis points</label>
              <input
                id="slippage"
                min="0"
                type="number"
                {...form.register("slippageBasisPoints", { valueAsNumber: true })}
              />
            </div>
            <div className="field-stack">
              <label htmlFor="commission">Commission per share, USD</label>
              <input
                id="commission"
                inputMode="decimal"
                {...form.register("commissionPerShareUsd", { required: true })}
              />
            </div>
            <div className="field-stack">
              <label htmlFor="minimum-commission">Minimum commission per order, USD</label>
              <input
                id="minimum-commission"
                inputMode="decimal"
                placeholder="0"
                {...form.register("minimumCommissionUsd")}
              />
              <span className="field-hint">
                Greater of the minimum and per-share fee; not added together.
              </span>
            </div>
          </div>
          {launch.error === null ? null : <ProblemNotice error={launch.error} />}
          <div className="button-row">
            <Button
              disabled={launch.isPending || selection.data === undefined || selection.isError}
              type="submit"
            >
              {launch.isPending ? "Launching…" : `Run revision ${bot.activeRevisionId.slice(0, 8)}`}
            </Button>
          </div>
        </form>
      ) : null}

      {history.isLoading ? (
        <ResourceState
          kind="loading"
          title="Loading history"
          description="Reading this Bot's durable backtest runs."
        />
      ) : null}
      {history.error !== null ? (
        <ResourceState
          kind="error"
          title="History unavailable"
          description="Backtest history could not be loaded."
        />
      ) : null}
      {history.data?.length === 0 ? (
        <ResourceState
          kind="empty"
          title="No backtests yet"
          description="Launch research from this Bot when it is ready."
        />
      ) : null}
      {(history.data?.length ?? 0) > 0 ? (
        <ol className="run-list">
          {history.data?.map((run) => {
            const terminal = ["cancelled", "completed", "failed"].includes(run.status);
            const confirmingDelete = deleteConfirmationId === run.id;
            const deleting = deleteBacktest.isPending && deleteBacktest.variables === run.id;
            return (
              <li key={run.id}>
                <Link to={`/bots/${bot.id}/backtests/${run.id}`}>
                  <div>
                    <strong>
                      {run.request.symbol || bot.symbol} · revision {run.revisionNumber || "—"}
                    </strong>
                    <span className={`status-pill status-pill--${run.status}`}>{run.status}</span>
                  </div>
                  <span>
                    {run.createdAt === ""
                      ? "Time unavailable"
                      : new Date(run.createdAt).toLocaleString()}{" "}
                    · {run.progressPercent}%
                  </span>
                </Link>
                <div className="button-row">
                  {run.status === "completed" ? (
                    <Link
                      className="button button--quiet"
                      to={`/tickers/${encodeURIComponent(run.request.symbol || bot.symbol)}?view=backtesting&backtestId=${encodeURIComponent(run.id)}`}
                    >
                      View trade chart
                    </Link>
                  ) : null}
                  {terminal && !confirmingDelete ? (
                    <Button
                      disabled={deleteBacktest.isPending}
                      onClick={() => {
                        deleteBacktest.reset();
                        setDeleteConfirmationId(run.id);
                      }}
                      tone="danger"
                    >
                      Delete result
                    </Button>
                  ) : null}
                </div>
                {confirmingDelete ? (
                  <div
                    aria-label={`Confirm deletion of backtest ${run.id}`}
                    className="delete-confirmation"
                    role="alertdialog"
                  >
                    <div>
                      <strong>Delete this backtest result permanently?</strong>
                      <p>The run record and its stored evidence cannot be recovered.</p>
                    </div>
                    <div className="button-row">
                      <Button
                        disabled={deleteBacktest.isPending}
                        onClick={() => setDeleteConfirmationId(undefined)}
                        tone="quiet"
                      >
                        Keep result
                      </Button>
                      <Button
                        disabled={deleteBacktest.isPending}
                        onClick={() =>
                          deleteBacktest.mutate(run.id, {
                            onSuccess: () => setDeleteConfirmationId(undefined),
                          })
                        }
                        tone="danger"
                      >
                        {deleting ? "Deleting…" : "Delete permanently"}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : null}
      {deleteBacktest.error === null ? null : <ProblemNotice error={deleteBacktest.error} />}
    </div>
  );
}
